import React from "react";

import Framework from "@beardeddevops/react.framework";
import {Parts, Types} from "components";
import ObjectOverride from "components/ObjectOverride";
import {Button} from "reactstrap";
import UniversalModalOverride from "components/UniversalModalOverride";
import DriversLicenseScan from "components/Objects/DriversLicenseScan";
import API from "@beardeddevops/react.api";
import moment from "moment";

/**
 *  @property {Customers_Object} state
 *  @property {object} model
 *  @property {Customers_Properties} model.properties
 *  @property {Customers_Is} model.is
 *  @property {Customers_Has} model.has
 */
class CustomerAddObject extends ObjectOverride {
	constructor(props) {
		super(props, Types.Customers._name);
		// noinspection JSPrimitiveTypeWrapperUsage
		this.model.properties.ethnicity_hispanic.required = false;

		this.afterLegalEntitySave = this.setNewObject('legal_entity', 'legal_entities');
	}

	formatDLScanDate = (date) => {
		if (date === null || typeof date === 'undefined' || date === '') {
			return date;
		}

		let formattedDate = moment(date);

		return formattedDate.isValid() ? formattedDate.format('YYYY-MM-DD') : date;
	}

	handleToggleDLScanModal = () => {
		this.setState({modalDLScan: !this.state.modalDLScan});
	}

	handleDLScanSave = (scan) => {
		let scannedCustomer = {};
		let scannedIdentification = {};
		let scannedFields = [
			'name_first',
			'name_middle',
			'name_last',
			'name_suffix',
			'weight',
			'street_1',
			'street_2',
			'city',
			'state',
			'zip',
			'gender',
			'birth_date',
			'height_ft',
			'height_in',
			'eye_color',
			'notes',
			'state_of_residence',
		];
		let scannedIdentificationFields = [
			'identification_authority',
			'identification_type',
			'identification_number',
			'identification_issue',
			'identification_expiration',
		];

		scannedFields.forEach((field) => {
			if (scan[field] !== null && typeof scan[field] !== 'undefined' && scan[field] !== '') {
				scannedCustomer[field] = field === 'birth_date' ? this.formatDLScanDate(scan[field]) : scan[field];
			}
		});

		scannedIdentificationFields.forEach((field) => {
			if (scan[field] !== null && typeof scan[field] !== 'undefined' && scan[field] !== '') {
				scannedIdentification[field] = ['identification_issue', 'identification_expiration'].includes(field) ? this.formatDLScanDate(scan[field]) : scan[field];
			}
		});

		if (!scannedCustomer.state_of_residence && scannedCustomer.state) {
			scannedCustomer.state_of_residence = scannedCustomer.state;
		}

		this.setState({
			...scannedCustomer,
			dlScanIdentification: scannedIdentification
		});
	}

	hasDLScanIdentification = () => {
		let identification = this.state.dlScanIdentification;

		return !!(identification && (
			identification.identification_authority ||
			identification.identification_type ||
			identification.identification_number ||
			identification.identification_issue ||
			identification.identification_expiration
		));
	}

	afterSubmit = async (customer) => {
		if (!this.hasDLScanIdentification()) {
			return false;
		}

		let identificationModel = new API.BuildClass(Types.Customers_Identifications._name, API.RequestType.CREATE);
		identificationModel.parent = customer.pkey;
		identificationModel.parent_binary_id = customer.binary_id;
		identificationModel.object.customer = customer.pkey;
		identificationModel.object.customer_binary_id = customer.binary_id;

		Object.keys(this.state.dlScanIdentification).forEach((field) => {
			identificationModel.object[field] = this.state.dlScanIdentification[field];
		});

		await identificationModel.submit(null, API.RequestType.CREATE, true);

		return false;
	}

	/**
	 * @param prevProps
	 * @param {Customers_Object} prevState
	 * @param snapshot
	 */
	componentDidUpdate(prevProps, prevState, snapshot) {
		super.componentDidUpdate(prevProps, prevState, snapshot);

		/** @type {Customers_Object} object */
		let object = this.state;

		if (typeof this.props.updateName === 'function') {
			this.props.updateName(object.name_first + " " + object.name_last);
		}
	}

	handleOptInToggle = (property) => {
		this.setState((state) => ({
			[property]: !state[property],
			[property + '_error']: '',
		}));
	}

	//CRM: block submit when this account has the CRM module and a required field is blank.
	//Mirrors the OnePage KRF pattern; mirrors the server's MissingCrmRequiredFields list.
	//Server remains the authority — this is client-side UX only.
	handleSubmit = (event, suppressToast = false, suppressProcess = false) => {
		if (this.props.rootState && this.props.rootState.crmAvailable) {
			const required = {
				name_first: 'First Name',
				name_last: 'Last Name',
				email: 'Email',
				phone: 'Phone Number',
				street_1: 'Address Street',
				city: 'Address City',
				state: 'Address State',
				zip: 'Address Zip',
				country: 'Address Country',
				birth_date: 'Birthdate',
				language: 'Language',
			};
			let hasMissing = false;
			let nextErrors = {};
			Object.keys(required).forEach((field) => {
				const value = this.state[field];
				if (value === null || typeof value === 'undefined' || String(value).trim() === '') {
					nextErrors[field + '_error'] = 'Required';
					hasMissing = true;
				} else if (this.state[field + '_error']) {
					nextErrors[field + '_error'] = '';
				}
			});
			if (hasMissing) {
				this.setState(nextErrors);
				return;
			}
		}
		super.handleSubmit(event, suppressToast, suppressProcess);
	}

	//Note: Doesn't need a form() function. Only ever used in a modal
	render() {
		/** @type {Customers_Properties} properties */
		let properties = this.model.properties;
		/** @type {Customers_Object} object */
		let object = this.state;
		let {loading} = this.state;
		const crmAvailable = !!(this.props.rootState && this.props.rootState.crmAvailable);

		return (
			<div>
				<fieldset>
					<div className={"row"}>
						<div className={"col-md"}>
							<h4 className="section-title">Customer Information</h4>
							<Parts.CustomerName
								object={object}
								properties={properties}
								onTextUpdate={this.handleTextFieldPropertyUpdate}
								crmAvailable={crmAvailable}
							/>

							<h4 className="section-title">Contact Information</h4>
							<div className="form-group">
								<Framework.Elements.PhoneNumber
									property={crmAvailable ? {...properties.phone, required: true} : properties.phone}
									value={object.phone}
									error={object.phone_error}
									update={this.handleTextFieldPropertyUpdate}
								/>
							</div>
							<div className="form-group">
								<Framework.Elements.EmailAddress
									property={crmAvailable ? {...properties.email, required: true} : properties.email}
									value={object.email}
									error={object.email_error}
									update={this.handleTextFieldPropertyUpdate}
								/>
							</div>
							{crmAvailable &&
								<div className="form-group">
									<Framework.Elements.Checkbox
										property={properties.email_opt_in}
										value={object.email_opt_in}
										error={object.email_opt_in_error}
										update={() => this.handleOptInToggle('email_opt_in')}
										label="Opt in to email marketing"
										color='primary'
									/>
								</div>}
							{crmAvailable &&
								<div className="form-group">
									<Framework.Elements.Checkbox
										property={properties.sms_opt_in}
										value={object.sms_opt_in}
										error={object.sms_opt_in_error}
										update={() => this.handleOptInToggle('sms_opt_in')}
										label="Opt in to text (SMS) messages"
										color='primary'
									/>
								</div>}

							{crmAvailable &&
								<div className="form-group">
									<Framework.Elements.Select
										shouldLoad={this.state.loaded}
										update={this.handleSelectPropertyUpdate}
										objects={[
											{pkey: 'English', name: 'English'},
											{pkey: 'Spanish', name: 'Spanish'},
											{pkey: 'Chinese', name: 'Chinese'},
											{pkey: 'Vietnamese', name: 'Vietnamese'},
											{pkey: 'Tagalog / Filipino', name: 'Tagalog / Filipino'},
											{pkey: 'Arabic', name: 'Arabic'},
											{pkey: 'French', name: 'French'},
											{pkey: 'Korean', name: 'Korean'},
											{pkey: 'Other', name: 'Other'},
										]}
										property={{...properties.language, required: true}}
										value={object.language}
										error={object.language_error}
										text={'name'}
										labelWidth={5}
										label="Language"
										limit={null}
										/>
										</div>}
										{crmAvailable &&
										<div className="form-group">
											<Framework.Elements.Date
												label={"Birth Date"}
												property={{...properties.birth_date, required: true}}
												value={object.birth_date}
												error={object.birth_date_error}
												update={this.handleTextFieldPropertyUpdate}
												disableFutureDates={true}
											/>
										</div>}
										</div>
										<div className={"col-md"}>
										<h4 className="section-title">Address</h4>
							<Parts.Address
								object={object}
								properties={properties}
								onTextUpdate={this.handleTextFieldPropertyUpdate}
								onSelectUpdate={this.handleSelectPropertyUpdate}
								crmAvailable={crmAvailable}
							/>

							<h4 className="section-title">Trust / Legal Entity Information</h4>
							<Parts.TransactionLegalEntity
								locations={this.props.locations}
								states={this.props.states}
								object={object}
								properties={properties}
								history={this.props.history}
								shouldLoad={this.state.loaded}
								onSelectUpdate={this.handleSelectPropertyUpdate}
								afterLegalEntitySave={this.afterLegalEntitySave}
							/>
						</div>
					</div>
				</fieldset>

				<div className="form-group" style={{textAlign: 'left'}}>
					<Button color="info" size="sm" onClick={this.handleToggleDLScanModal}>
						<i className={"fa fa-automobile"}/>&nbsp;&nbsp;DL Scan
					</Button>
				</div>

				<Framework.Elements.LoadingSpinner isLoading={loading}/>

				<UniversalModalOverride
					locations={this.props.locations}
					states={this.props.states}
					history={this.props.history}
					name={"Drivers License Scan"}
					title={"Waiting for Drivers License Barcode Scan"}
					object={DriversLicenseScan}
					showModal={this.state.modalDLScan}
					handleToggle={this.handleToggleDLScanModal}
					afterSave={this.handleDLScanSave}
					hideGoTo={true}
					cancelLabel={"Close"}
					submitLabel={"Use Scan"}
					objectProps={{
						afterScan: this.handleDLScanSave
					}}
				/>
			</div>
		)
	}
}

export default Framework.Components.withRootState(CustomerAddObject);
